import { PDFDocument, PDFFont, PDFPage, StandardFonts, degrees, rgb } from 'pdf-lib';

import type { PaymentDetails } from '@/lib/api/payments';

export interface RefundReceiptLabels {
  title: string;
  customer: string;
  customerEmail: string;
  orderId: string;
  paymentId: string;
  originalPaymentDate: string;
  originalPaymentAmount: string;
  paymentMethod: string;
  refundDate: string;
  refundTransactionId: string;
  amount: string;
  totalRefunded: string;
  generatedAt: string;
  testWatermark: string;
  notAvailable: string;
}

export interface RefundReceiptFormatters {
  formatMoney: (minorUnits: number, currencyCode: string) => string;
  formatDate: (date: Date | string) => string;
}

export interface BuildRefundReceiptInput {
  payment: PaymentDetails;
  labels: RefundReceiptLabels;
  formatters: RefundReceiptFormatters;
}

const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN_X = 40;
const MARGIN_TOP = 40;
const MARGIN_BOTTOM = 50;

const COLOR_TEXT = rgb(0.13, 0.13, 0.13);
const COLOR_MUTED = rgb(0.45, 0.45, 0.45);
const COLOR_RULE = rgb(0.85, 0.85, 0.85);
const COLOR_WATERMARK = rgb(0.95, 0.32, 0.32);

// StandardFonts use WinAnsi encoding. Anything outside WinAnsi will throw at
// draw time, which would break the receipt for customers with e.g. CJK names.
// Replace unsupported code points with '?' up-front.
const WINANSI_EXTRAS = new Set<number>([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039, 0x0152,
  0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a,
  0x0153, 0x017e, 0x0178,
]);

function sanitize(text: string): string {
  let out = '';
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    const supported =
      (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff) || WINANSI_EXTRAS.has(code);
    out += supported ? ch : '?';
  }
  return out;
}

function truncate(font: PDFFont, size: number, text: string, maxWidth: number): string {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) return text;
  const ellipsis = '…';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (font.widthOfTextAtSize(text.slice(0, mid) + ellipsis, size) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + ellipsis;
}

interface RefundRow {
  id: string;
  amount: number;
  currency: string;
  createdAt: Date;
}

function extractRefunds(payment: PaymentDetails): RefundRow[] {
  return payment.transactions
    .filter((t) => t.type === 'refund' && t.status === 'succeeded')
    .map((t) => ({
      id: t.id,
      amount: t.amount,
      currency: t.currency || payment.currency,
      createdAt: t.createdAt instanceof Date ? t.createdAt : new Date(t.createdAt as unknown as string),
    }))
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

function customerFullName(payment: PaymentDetails, fallback: string): string {
  const first = (payment.customer?.firstName ?? '').trim();
  const last = (payment.customer?.lastName ?? '').trim();
  const name = `${first} ${last}`.trim();
  return name.length > 0 ? name : fallback;
}

function paymentMethodLabel(payment: PaymentDetails): string | null {
  const card = payment.paymentInstrument?.card;
  if (!card?.brand || !card?.last4) return null;
  return `${card.brand} ****${card.last4}`;
}

function orderDisplay(payment: PaymentDetails, fallback: string): string {
  return payment.orderId || payment.order?.externalOrderId || fallback;
}

export async function buildRefundReceiptPdf(input: BuildRefundReceiptInput): Promise<Uint8Array> {
  const { payment, labels, formatters } = input;

  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const page = doc.addPage([PAGE_W, PAGE_H]);

  let y = PAGE_H - MARGIN_TOP;

  // — Header —
  y -= 20;
  page.drawText(sanitize(labels.title), {
    x: MARGIN_X,
    y,
    size: 22,
    font: bold,
    color: COLOR_TEXT,
  });

  const storeName = payment.store?.name?.trim();
  if (storeName) {
    const text = sanitize(storeName);
    const size = 13;
    const width = bold.widthOfTextAtSize(text, size);
    page.drawText(text, {
      x: PAGE_W - MARGIN_X - width,
      y: y + 4,
      size,
      font: bold,
      color: COLOR_TEXT,
    });
  }

  y -= 18;
  drawRule(page, y);

  // — Meta rows —
  const metaRows = buildMetaRows(payment, labels, formatters);
  y -= 22;
  for (const row of metaRows) {
    drawMetaRow(page, font, bold, row.label, row.value, y);
    y -= 20;
  }

  // — Refund table —
  y -= 6;
  drawRule(page, y);
  y -= 22;
  drawTableHeader(page, bold, labels, y);
  y -= 6;
  drawRule(page, y);
  y -= 18;

  const refunds = extractRefunds(payment);
  if (refunds.length > 0) {
    for (const refund of refunds) {
      drawRefundRow(page, font, refund, formatters, y);
      y -= 18;
    }
  } else {
    // Edge: refundedAmount > 0 but no explicit refund transactions.
    drawRefundRow(
      page,
      font,
      {
        id: labels.notAvailable,
        amount: payment.refundedAmount,
        currency: payment.currency,
        createdAt: payment.updatedAt instanceof Date ? payment.updatedAt : new Date(),
      },
      formatters,
      y,
    );
    y -= 18;
  }

  // — Total —
  y -= 6;
  drawRule(page, y);
  y -= 22;
  drawTotal(page, bold, labels, formatters, payment, y);

  // — Footer —
  const footerText = sanitize(
    labels.generatedAt.replace('{{date}}', formatters.formatDate(new Date())),
  );
  const footerSize = 9;
  const footerWidth = font.widthOfTextAtSize(footerText, footerSize);
  page.drawText(footerText, {
    x: (PAGE_W - footerWidth) / 2,
    y: MARGIN_BOTTOM - 20,
    size: footerSize,
    font,
    color: COLOR_MUTED,
  });

  // — Test watermark —
  if (payment.draft) {
    drawWatermark(page, bold, labels.testWatermark);
  }

  return doc.save();
}

interface MetaRow {
  label: string;
  value: string;
}

function buildMetaRows(
  payment: PaymentDetails,
  labels: RefundReceiptLabels,
  formatters: RefundReceiptFormatters,
): MetaRow[] {
  const rows: MetaRow[] = [
    { label: labels.customer, value: customerFullName(payment, labels.notAvailable) },
  ];

  const email = payment.customer?.email?.trim();
  if (email) rows.push({ label: labels.customerEmail, value: email });

  rows.push({ label: labels.orderId, value: orderDisplay(payment, labels.notAvailable) });
  rows.push({ label: labels.paymentId, value: payment.id });
  rows.push({
    label: labels.originalPaymentDate,
    value: formatters.formatDate(payment.createdAt),
  });
  rows.push({
    label: labels.originalPaymentAmount,
    value: formatters.formatMoney(payment.amount, payment.currency),
  });

  const method = paymentMethodLabel(payment);
  if (method) rows.push({ label: labels.paymentMethod, value: method });

  return rows;
}

function drawMetaRow(
  page: PDFPage,
  font: PDFFont,
  bold: PDFFont,
  label: string,
  value: string,
  y: number,
): void {
  const size = 11;
  const labelText = sanitize(label);
  page.drawText(labelText, { x: MARGIN_X, y, size, font: bold, color: COLOR_MUTED });

  const valueX = MARGIN_X + 170;
  const valueMaxWidth = PAGE_W - MARGIN_X - valueX;
  const valueText = truncate(font, size, sanitize(value), valueMaxWidth);
  page.drawText(valueText, { x: valueX, y, size, font, color: COLOR_TEXT });
}

const TABLE_COL_DATE_W = 130;
const TABLE_COL_ID_W = 240;
const TABLE_GAP = 15;

function drawTableHeader(page: PDFPage, bold: PDFFont, labels: RefundReceiptLabels, y: number): void {
  const size = 10;
  page.drawText(sanitize(labels.refundDate), {
    x: MARGIN_X,
    y,
    size,
    font: bold,
    color: COLOR_MUTED,
  });
  page.drawText(sanitize(labels.refundTransactionId), {
    x: MARGIN_X + TABLE_COL_DATE_W + TABLE_GAP,
    y,
    size,
    font: bold,
    color: COLOR_MUTED,
  });
  const amountLabel = sanitize(labels.amount);
  const amountWidth = bold.widthOfTextAtSize(amountLabel, size);
  page.drawText(amountLabel, {
    x: PAGE_W - MARGIN_X - amountWidth,
    y,
    size,
    font: bold,
    color: COLOR_MUTED,
  });
}

function drawRefundRow(
  page: PDFPage,
  font: PDFFont,
  refund: RefundRow,
  formatters: RefundReceiptFormatters,
  y: number,
): void {
  const size = 10;

  const dateText = truncate(font, size, sanitize(formatters.formatDate(refund.createdAt)), TABLE_COL_DATE_W);
  page.drawText(dateText, { x: MARGIN_X, y, size, font, color: COLOR_TEXT });

  const idText = truncate(font, size, sanitize(refund.id), TABLE_COL_ID_W);
  page.drawText(idText, {
    x: MARGIN_X + TABLE_COL_DATE_W + TABLE_GAP,
    y,
    size,
    font,
    color: COLOR_TEXT,
  });

  const amountText = sanitize(formatters.formatMoney(refund.amount, refund.currency));
  const amountWidth = font.widthOfTextAtSize(amountText, size);
  page.drawText(amountText, {
    x: PAGE_W - MARGIN_X - amountWidth,
    y,
    size,
    font,
    color: COLOR_TEXT,
  });
}

function drawTotal(
  page: PDFPage,
  bold: PDFFont,
  labels: RefundReceiptLabels,
  formatters: RefundReceiptFormatters,
  payment: PaymentDetails,
  y: number,
): void {
  const size = 12;
  const totalLabel = sanitize(labels.totalRefunded);
  const totalValue = sanitize(formatters.formatMoney(payment.refundedAmount, payment.currency));
  const totalValueWidth = bold.widthOfTextAtSize(totalValue, size);
  const totalLabelWidth = bold.widthOfTextAtSize(totalLabel, size);
  const gap = 12;

  const valueX = PAGE_W - MARGIN_X - totalValueWidth;
  const labelX = valueX - gap - totalLabelWidth;

  page.drawText(totalLabel, { x: labelX, y, size, font: bold, color: COLOR_TEXT });
  page.drawText(totalValue, { x: valueX, y, size, font: bold, color: COLOR_TEXT });
}

function drawRule(page: PDFPage, y: number): void {
  page.drawLine({
    start: { x: MARGIN_X, y },
    end: { x: PAGE_W - MARGIN_X, y },
    thickness: 0.5,
    color: COLOR_RULE,
  });
}

function drawWatermark(page: PDFPage, bold: PDFFont, label: string): void {
  const text = sanitize(label);
  const size = 96;
  const width = bold.widthOfTextAtSize(text, size);
  page.drawText(text, {
    x: (PAGE_W - width * 0.7) / 2,
    y: PAGE_H / 2 - 60,
    size,
    font: bold,
    color: COLOR_WATERMARK,
    opacity: 0.18,
    rotate: degrees(-30),
  });
}
